import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { MODULE_NAME as MODULE_NAME_LOC, ACTIONS as ACTIONS_LOC } from '@/store/locations/constants'
import layerService from '@/api/layerService'
import ShopsClusterLayer from '@/api/ShopClusterLayer'
import { shopIconUrl, shopIconOutlineUrl } from '@/utils/mapUtils'
import debounce, { DEBOUNCE_TIME } from '@/utils/debounce'
import hex2Rgb from '@/utils/hex2rgb'
import exploreService from '@/api/exploreService'
import { MODULE_NAME as MODULE_NAME_FILTER, GETTERS as GETTERS_FILTER } from '@/store/filters/constants'
import { MODULE_NAME as MODULE_NAME_MAP, MUTATIONS as MUTATIONS_MAP } from '@/store/map/constants'
import dashboardService from '@/api/dashboardService'
import { getDefaultState } from '@/store/locations/state'
import { ScatterplotLayer } from '@deck.gl/layers'

function getAreaName (areaType) {
  return getDefaultState()[areaType].meta.name
}

function generateSteps ({ min, max, numsteps }) {
  const diff = (max - min) / numsteps
  return [
    min,
    min + (diff * 1),
    min + (diff * 2),
    min + (diff * 3),
    min + (diff * 4),
    max
  ]
}

const AREA_TYPE_OPTIONS = [
  { id: 'zones', name: 'Zonas' },
  { id: 'adverts', name: 'Soportes' },
  { id: 'pois', name: 'Competidores' }
]

const LEGEND_BUTTONS = [
  { type: 'button', id: 'all', name: 'Todo' },
  { type: 'button', id: '10', name: 'Top 10' },
  { type: 'button', id: '5', name: 'Top 5' }
]

const LEGEND_STEPS = [
  { type: 'range', id: 0, color: '#b0f2bc' },
  { type: 'range', id: 1, color: '#77e2a8' },
  { type: 'range', id: 2, color: '#4cc8a3' },
  { type: 'range', id: 3, color: '#31a6a3' },
  { type: 'range', id: 4, color: '#267e98' }
]

// const ZIPCODE_COLOR = [22, 150, 157, 25]
const ZIPCODE_BORDER_COLOR = [22, 150, 157, 255]

export default {
  name: 'MapWidget',
  mixins: [],
  components: {
    MapComponent: () => import(/* webpackChunkName: "MapComponent" */ '@/components/map-component/MapComponent.vue'),
    MapWidgetLegend: () => import(/* webpackChunkName: "MapWidgetLegend" */ './map-widget-legend/MapWidgetLegend.vue'),
    TagListSelector: () => import(/* webpackChunkName: "TagListSelector" */ '@/components/tag-list-selector/TagListSelector.vue')
  },
  props: {
    title: {
      type: String,
      required: true
    },
    primaryIndicator: {
      type: String,
      required: true,
      default: ''
    },
    primaryIndicatorLabel: {
      type: String,
      required: true,
      default: ''
    }
  },
  data () {
    return {
      shopsLayerId: 'shops-layer-map-widget',
      advertsLayerId: 'adverts-layer-map-widget',
      poisLayerId: 'pois-layer-map-widget',
      zonesLayerId: 'zones-layer-map-widget',
      loading: true,
      areaTypes: AREA_TYPE_OPTIONS,
      selectedAreaType: AREA_TYPE_OPTIONS[0],
      legendSteps: LEGEND_STEPS,
      legendButtons: LEGEND_BUTTONS,
      selectedLegendItem: null,
      hoveredZipcodeId: null,
      hoveredPOIId: null,
      hoveredAdvertId: null,
      sidebarExpanded: false,
      areaTemplateSaved: false,
      areaTemplateName: '',
      areaTemplateLoading: false,
      areas: {
        data: [],
        meta: {}
      },
      selectedAreaGroups: [],
      selectedShop: null,
      allCampaigns: false
    }
  },
  computed: {
    ...mapState(MODULE_NAME_LOC, ['shops']),
    ...mapGetters(MODULE_NAME_FILTER, {
      filters: GETTERS_FILTER.GET_ROUTE_FILTERS
    }),
    selectedAreas () {
      const group = this.selectedAreaGroups.find(g => g.id === this.selectedShopId)
      return group ? group.areas : []
    },
    otherAreaGroups () {
      return this.selectedAreaGroups.filter(g => g.id !== this.selectedShopId && g.areas.length)
    },
    shopOptions () {
      return this.shops.data.map(s => ({ ...s, name: s.label }))
    },
    selectedShopId () {
      return this.selectedShop && this.selectedShop.value
    },
    channelType () {
      return this.$route.params.type
    },
    legendStats () {
      const meta = this.areas.meta
      const key = this.primaryIndicator.toLowerCase()
      const max = meta[`${key}_max`] || 0
      const min = meta[`${key}_min`] || 0
      return {
        min: min ? Number(min.toFixed(2)) : min,
        max: max ? Number(max.toFixed(2)) : max,
        steps: generateSteps({ min, max, numsteps: 5 })
      }
    },
    legendButtonsWithRanges () {
      const disabled = !this.legendStats.min && !this.legendStats.max
      const key = this.primaryIndicator.toLowerCase()
      return LEGEND_BUTTONS.map(d => ({
        ...d,
        disabled,
        range_min: d.id === 'all' ? null : this.areas.meta[`${key}_${d.id}`],
        range_max: d.id === 'all' ? null : this.legendStats.max
      }))
    },
    legendStepsWithColor () {
      return LEGEND_STEPS.map((s, i) => ({
        ...s,
        range_min: this.legendStats.steps[i],
        range_max: this.legendStats.steps[i + 1]
      }))
    },
    legendTitle () {
      return `Número de ${this.primaryIndicatorLabel}`
    },
    startDateFilter () {
      return this.filters.find(f => f.key === 'start_date')
    },
    finishDateFilter () {
      return this.filters.find(f => f.key === 'finish_date')
    }
  },
  watch: {
    hoveredZipcodeId () {
      this.zonesLayerDraw()
    },
    hoveredPOIId () {
      this.poisLayerDraw()
    },
    hoveredAdvertId () {
      this.advertsLayerDraw()
    },
    primaryIndicator () {
      this.drawAreaLayer()
    },
    selectedAreas () {
      this.drawAreaLayer()
    },
    filters () {
      if (this.startDateFilter.value !== this.finishDateFilter.value) {
        this.updateAreas()
      }
    }
  },
  created () {
    this.areaSearch = debounce(this._areaSearch, DEBOUNCE_TIME)
  },
  beforeDestroy () {
    layerService.removeLayer(this.shopsLayerId)
    this.cleanLayers()
  },
  methods: {
    ...mapActions(MODULE_NAME_LOC, Object.values(ACTIONS_LOC)),
    ...mapMutations(MODULE_NAME_MAP, [MUTATIONS_MAP.FLY_TO]),
    cleanLayers () {
      layerService.removeLayer(this.advertsLayerId)
      layerService.removeLayer(this.poisLayerId)
      layerService.removeLayer(this.zonesLayerId)
    },

    async onMapLoaded () {
      await this[ACTIONS_LOC.FETCH_SHOPS]({ reset: true, page_size: 10000, summary: 1, ordering: 'name' })
      await this.selectShop(this.shops.data[0])
    },

    async updateAreas () {
      this.loading = true
      try {
        await this._areaSearch()
      } catch (err) {
        // eslint-disable-next-line
        console.error('[map-widget.js] Error searching for areas', err)
      }
      await this.$nextTick()
      try {
        this.cleanLayers()
        await this.drawAreaLayer()
        this.toggleLayers()
      } catch (err) {
        // eslint-disable-next-line
        console.error('[map-widget.js] Error drawing area layer', err)
      }
      this.loading = false
    },

    async selectAreaType (opt) {
      this.areas.meta = {}
      this.selectedAreaType = opt
      this.clearAllAreas()
      this.cleanLayers()
      this.$refs.select.close()
      await this.$nextTick()
      await this.updateAreas()
    },

    async _areaSearch (query) {
      const type = this.selectedAreaType.id
      const params = {
        q: query,
        stats: 1,
        query: 1,
        stats_type: this.channelType,
        page_size: 10,
        stats_campaigns_shops__in: this.selectedShopId,
        stats_all: this.allCampaigns
        // results: 0,
        // count: 0,
        // campaign_status: 'ACTIVE,FINISHED'
      }

      const areas = await dashboardService.getLocations(params, this.filters, type)

      areas.meta.name = getAreaName(type)
      this.areas = areas
    },

    toggleLayers () {
      const map = {
        adverts: this.advertsLayerId,
        pois: this.poisLayerId,
        zones: this.zonesLayerId
      }
      const type = this.selectedAreaType.id
      const activeLayer = map[type]
      for (const key in map) {
        const id = map[key]
        if (layerService.hasLayer(id)) {
          if (id === activeLayer) {
            layerService.showLayer(id)
          } else {
            layerService.hideLayer(id)
          }
        }
      }
    },

    drawAreaLayer () {
      if (!this.areas.meta.count) {
        this.cleanLayers()
        return
      }

      const type = this.selectedAreaType.id
      const method = this[`${type}LayerDraw`]
      if (method) {
        return method()
      }
    },

    drawShopsLayer () {
      if (layerService.hasLayer(this.shopsLayerId)) {
        layerService.updateLayer(
          this.shopsLayerId,
          { data: [this.shops.data.find(d => d.value === this.selectedShopId)] }
        )
      } else {
        layerService.addLayer({
          id: this.shopsLayerId,
          data: [this.shops.data.find(d => d.value === this.selectedShopId)],
          layerType: ShopsClusterLayer,
          sizeScale: 46,
          textSizeScale: 24,
          pickable: true,
          getPosition: d => [d.lng, d.lat],
          getIcon: d => ({
            width: 40,
            height: 40,
            url: d.properties.cluster ? shopIconOutlineUrl : shopIconUrl
          }),
          getTooltip ({ object }) {
            const count = object.properties.point_count
            const label = object.properties.label
            const text = count > 1 ? `${count} centros` : label
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { text, style, className: 'map-popup-white' }
          }
        })
      }
    },

    async poisLayerDraw () {
      if (layerService.hasLayer(this.poisLayerId)) {
        layerService.updateLayer(this.poisLayerId, {
          updateTriggers: {
            getFillColor: [this.hoveredPOIId, this.selectedAreas, this.primaryIndicator],
            getLineColor: [this.hoveredPOIId, this.selectedAreas, this.primaryIndicator],
            getLineWidth: [this.hoveredPOIId, this.selectedAreas, this.primaryIndicator]
          }
        })
      } else if (this.areas.query) {
        await layerService.addSQLLayer({
          id: this.poisLayerId,
          sql: this.areas.query,
          sublayer: ScatterplotLayer,
          getPosition: d => d.geometry.coordinates,
          radiusUnits: 'pixels',
          pickable: true,
          stroked: true,
          filled: true,
          getRadius: 8,
          getLineColor: (object) => {
            return this.getAreaStyle(object, this.hoveredPOIId).borderColor
          },
          getLineWidth: (object) => {
            return this.getAreaStyle(object, this.hoveredPOIId).borderWidth
          },
          getFillColor: (object) => {
            return this.getAreaStyle(object, this.hoveredPOIId).color
          },
          getTooltip: ({ object }) => {
            const html = this.getTooltipHTML(object)
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { html, style, className: 'map-popup-white' }
          },
          updateTriggers: {
            getFillColor: [this.hoveredPOIId, this.selectedAreas, this.primaryIndicator],
            getLineColor: [this.hoveredPOIId, this.selectedAreas, this.primaryIndicator],
            getLineWidth: [this.hoveredPOIId, this.selectedAreas, this.primaryIndicator]
          },
          onHover: ({ object }) => {
            this.hoveredPOIId = (object && object.properties.cartodb_id) || null
          },
          onClick: ({ object }) => {
            const id = object.properties.id || object.properties.cartodb_id
            const area = { ...object.properties, id }
            this.selectArea(area)
          }
        })
      }
    },

    async advertsLayerDraw () {
      if (layerService.hasLayer(this.advertsLayerId)) {
        layerService.updateLayer(this.advertsLayerId, {
          updateTriggers: {
            getFillColor: [this.hoveredAdvertId, this.selectedAreas, this.primaryIndicator],
            getLineColor: [this.hoveredAdvertId, this.selectedAreas, this.primaryIndicator],
            getLineWidth: [this.hoveredAdvertId, this.selectedAreas, this.primaryIndicator]
          }
        })
      } else if (this.areas.query) {
        await layerService.addSQLLayer({
          id: this.advertsLayerId,
          sql: this.areas.query,
          sublayer: ScatterplotLayer,
          getPosition: d => d.geometry.coordinates,
          radiusUnits: 'pixels',
          pickable: true,
          stroked: true,
          filled: true,
          getRadius: 8,
          getLineColor: (object) => {
            return this.getAreaStyle(object, this.hoveredAdvertId).borderColor
          },
          getLineWidth: (object) => {
            return this.getAreaStyle(object, this.hoveredAdvertId).borderWidth
          },
          getFillColor: (object) => {
            return this.getAreaStyle(object, this.hoveredAdvertId).color
          },
          getTooltip: ({ object }) => {
            const html = this.getTooltipHTML(object)
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { html, style, className: 'map-popup-white' }
          },
          updateTriggers: {
            getFillColor: [this.hoveredAdvertId, this.selectedAreas, this.primaryIndicator],
            getLineColor: [this.hoveredAdvertId, this.selectedAreas, this.primaryIndicator],
            getLineWidth: [this.hoveredAdvertId, this.selectedAreas, this.primaryIndicator]
          },
          onHover: ({ object }) => {
            this.hoveredAdvertId = (object && object.properties.cartodb_id) || null
          },
          onClick: ({ object }) => {
            const id = object.properties.id || object.properties.cartodb_id
            const area = { ...object.properties, id, name: object.properties.address }
            this.selectArea(area)
          }
        })
      }
    },

    async zonesLayerDraw () {
      if (layerService.hasLayer(this.zonesLayerId)) {
        layerService.updateLayer(this.zonesLayerId, {
          updateTriggers: {
            getFillColor: [this.hoveredZipcodeId, this.selectedAreas, this.primaryIndicator],
            getLineColor: [this.hoveredZipcodeId, this.selectedAreas, this.primaryIndicator],
            getLineWidth: [this.hoveredZipcodeId, this.selectedAreas, this.primaryIndicator]
          }
        })
      } else {
        await layerService.addSQLLayer({
          id: this.zonesLayerId,
          sql: this.areas.query,
          zIndex: -1,
          pickable: true,
          getLineColor: (object) => {
            return this.getAreaStyle(object, this.hoveredZipcodeId).borderColor
          },
          getLineWidth: (object) => {
            return this.getAreaStyle(object, this.hoveredZipcodeId).borderWidth
          },
          getFillColor: (object) => {
            return this.getAreaStyle(object, this.hoveredZipcodeId).color
          },
          getTooltip: ({ object }) => {
            const html = this.getTooltipHTML(object)
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { html, style, className: 'map-popup-white' }
          },
          updateTriggers: {
            getFillColor: [this.hoveredZipcodeId, this.selectedAreas, this.primaryIndicator],
            getLineColor: [this.hoveredZipcodeId, this.selectedAreas, this.primaryIndicator],
            getLineWidth: [this.hoveredZipcodeId, this.selectedAreas, this.primaryIndicator]
          },
          onHover: ({ object }) => {
            this.hoveredZipcodeId = (object && object.properties.cartodb_id) || null
          },
          onClick: ({ object }) => {
            const id = object.properties.id || object.properties.cartodb_id
            const area = { ...object.properties, id }
            this.selectArea(area)
          }
        })
      }
    },

    getAreaStyle (feature, hoveredId) {
      const key = this.primaryIndicator.toLowerCase()
      const value = Number(feature.properties[key])
      const step = this.legendStepsWithColor.find(s => {
        return value >= s.range_min && value <= s.range_max
      })

      if (!step) {
        return {
          color: [0, 0, 0, 255],
          borderColor: [0, 0, 0, 255],
          borderWidth: 1
        }
      }

      const id = feature.properties.id || feature.properties.cartodb_id
      const hovered = id === hoveredId
      const selected = this.selectedAreas.find(z => z.id === id)

      let opacity = 150
      let borderColor = ZIPCODE_BORDER_COLOR
      let borderWidth = 1

      if (hovered) {
        opacity = 180
      }
      if (selected) {
        opacity = 255
      }
      if (selected || hovered) {
        borderWidth = 2
        borderColor = [0, 0, 0]
      }

      const rgb = (step && hex2Rgb(step.color)) || [0, 0, 0]
      const color = [...rgb, opacity]

      return { color, borderColor, borderWidth }
    },

    getTooltipHTML (feature) {
      const key = this.primaryIndicator.toLowerCase()
      const value = feature.properties[key]
      const valueFormatted = Number(value).toFixed(key === 'ctr' ? 2 : 0)
      const name = feature.properties.name || feature.properties.label || feature.properties.address
      return `
        <div>
          <p style="font-size: 12px; margin-bottom: 2px">
            ${this.areas.meta.name} ${name}
          </p>
          <p>
            <strong style="font-weight: 600">${valueFormatted}</strong>
            <small style="font-size: 14px">${this.primaryIndicatorLabel}</small>
          </p>
        </div>
      `
    },

    selectAreas (areas, clearLegend = true) {
      if (areas.length === 1 && areas[0].id === 'all') {
        return this.onLegendSelect(LEGEND_BUTTONS[0])
      }

      const group = this.selectedAreaGroups.find(g => g.id === this.selectedShopId)
      if (group) {
        this.$set(group, 'areas', areas)
      }
      if (clearLegend) {
        this.selectedLegendItem = null
      }
    },

    isAreaSelected (area) {
      return this.selectedAreas.some(a => a.id === area.id)
    },

    selectArea (area) {
      const newAreas = this.isAreaSelected(area)
        ? this.selectedAreas.filter(a => a.id !== area.id)
        : this.selectedAreas.concat(area)

      this.selectAreas(newAreas)
    },

    async onToggleAllCampaigns () {
      this.allCampaigns = !this.allCampaigns
      const allSelected = this.selectedLegendItem && this.selectedLegendItem.id === LEGEND_BUTTONS[0].id
      await this.updateAreas()
      if (allSelected) {
        await this.onLegendSelect(LEGEND_BUTTONS[0])
      }
      this.updateCurrentGroupCount()
    },

    async onLegendSelect (btn) {
      this.loading = true
      try {
        this.selectedLegendItem = btn
        const key = this.primaryIndicator.toLowerCase()
        const type = this.selectedAreaType.id
        const params = btn.id === 'all' ? {
          stats: 1,
          stats_all: this.allCampaigns,
          stats_type: this.channelType,
          stats_campaigns_shops__in: this.selectedShopId,
          // stats: 0,
          // in_shop_influence_area: this.selectedShopId,
          page_size: 10000
        } : {
          stats: 1,
          stats_all: false,
          stats_type: this.channelType,
          stats_campaigns_shops__in: this.selectedShopId,
          page_size: 10000
        }
        if (this.selectedLegendItem.range_min) {
          params[`${key}__gte`] = this.selectedLegendItem.range_min
        }
        if (this.selectedLegendItem.range_max) {
          params[`${key}__lte`] = this.selectedLegendItem.range_max
        }

        const areas = await dashboardService.getLocations(params, this.filters, type)
        this.selectAreas(areas.data, false)
      } catch (err) {
        // eslint-disable-next-line
        console.error('[map-widget.js] Error finding area interval', err)
      }
      this.loading = false
    },
    toggleSidebar () {
      this.sidebarExpanded = !this.sidebarExpanded
    },
    async saveAreaTemplate () {
      this.areaTemplateLoading = true
      const typeMap = {
        zones: 'postalcode',
        adverts: 'customerregion',
        pois: 'poi'
      }
      try {
        const shops = this.selectedAreaGroups.map(g => g.id)
        const areas = this.selectedAreaGroups
          .map(g => g.areas)
          .flat()
          .filter(a => a.id !== 'all')

        await exploreService.saveGeoTemplate({
          name: this.areaTemplateName,
          type: typeMap[this.selectedAreaType.id],
          geometries: areas,
          shops: shops
        })
        this.areaTemplateSaved = true
      } catch (err) {
        // eslint-disable-next-line
        console.error('[map-widget.js] Error saving area template', err)
      }
      this.areaTemplateLoading = false
    },
    finishAreaTemplate () {
      this.areaTemplateSaved = false
      this.sidebarExpanded = false
      this.clearAllAreas()
    },
    async selectShop (shop) {
      if (!this.selectedAreaGroups.some(g => g.id === shop.value)) {
        this.selectedAreaGroups.push({
          id: shop.value,
          name: shop.label,
          areas: []
        })
      }

      this.selectedShop = shop
      this.$refs.shopSelector.close()
      this.selectedLegendItem = null

      await this.$nextTick()
      this.drawShopsLayer()
      this.cleanLayers()
      this.flyToShop()
      await this.updateAreas()
      await this.$nextTick()
      this.updateCurrentGroupCount()
    },
    updateCurrentGroupCount () {
      const group = this.selectedAreaGroups.find(g => g.id === this.selectedShop.value)
      this.$set(group, 'count', this.areas.meta.count)
    },
    flyToShop () {
      this[MUTATIONS_MAP.FLY_TO]({
        zoom: 8,
        longitude: this.selectedShop.lng,
        latitude: this.selectedShop.lat
      })
    },
    clearAllAreas () {
      for (const group of this.selectedAreaGroups) {
        group.areas = []
      }
      for (const group of this.otherAreaGroups) {
        this.clearGroup(group)
      }
    },
    clearGroup (group) {
      this.selectedAreaGroups = this.selectedAreaGroups.filter(g => g.id !== group.id)
    },
    clearArea (area, group) {
      const newAreas = group.areas.filter(a => a.id !== area.id)
      this.$set(group, 'areas', newAreas)
    }
  }
}
